import { Component, Input, OnChanges } from '@angular/core';
import { tryParseJson, unwrapJson } from '../pos-json.helpers';

interface AmountSide {
  documentTotal?: number;
  lineTotal?: number;
  paymentTotal?: number;
  installmentCount?: number;
  /** Karta iade edilen tutar (iptal). */
  refundTotal?: number;
}

/** Pavo ödeme kalemi: OperationTypeId 2 karta iade; durum 2/10 tamamlandı. */
const REFUND_OPERATION = 2;
const COMPLETED_PAYMENT_STATUSES = [2, 10];

export interface AmountCompareRow {
  label: string;
  requested?: number;
  responded?: number;
  /** Para tutarı mı (2 hane) yoksa adet mi (taksit). */
  isMoney: boolean;
}

/** Kuruş yuvarlaması fark sayılmaz. */
const TOLERANCE = 0.005;

const moneyFormat = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Sağlayıcıya gönderilen tutarlarla cihazın/sağlayıcının döndürdüğü tutarları yan yana koyar:
 * belge toplamı, satırların toplamı, kart ödemesi ve taksit. İstek bizim PosSaleRequest /
 * PosVoidRequest modelimiz; cevap sağlayıcıya göre değişir (Pavo link zarfı, terminal cevabı),
 * bu yüzden cevaptaki satış nesnesi gömülü JSON'lar açılarak aranır.
 */
@Component({
  selector: 'app-pos-amount-compare',
  templateUrl: './pos-amount-compare.component.html',
})
export class PosAmountCompareComponent implements OnChanges {
  @Input() requestJson?: string | null;
  @Input() responseJson?: string | null;

  rows: AmountCompareRow[] = [];

  ngOnChanges(): void {
    const requestBody = this.parse(this.requestJson);
    const response = this.readResponse(this.parse(this.responseJson));

    // Satır iadesi isteği iade tutarını ve satır numaralarını taşır; cevabın biçimi (iade kalemi
    // ya da iade satışının kendi ödemesi) cihazda kesinleşecek — ikisinden biri okunur (K24).
    if (this.isRefundRequest(requestBody)) {
      const amount = this.number(requestBody.Amount);
      this.rows = [
        { label: 'İade tutarı', requested: amount, responded: response.refundTotal ?? response.paymentTotal, isMoney: true },
      ].filter(row => row.requested !== undefined || row.responded !== undefined);
      return;
    }

    // İptal isteği (PosVoidRequest) yalnızca iptal edilecek tutarı taşır: bu tutar karta
    // iade edilen tutarla ve iptal edilen satışın toplamıyla karşılaştırılır.
    if (this.isVoidRequest(requestBody)) {
      const amount = this.number(requestBody.Amount);
      this.rows = [
        { label: 'İade tutarı', requested: amount, responded: response.refundTotal, isMoney: true },
        { label: 'İptal edilen satış', requested: amount, responded: response.documentTotal, isMoney: true },
      ].filter(row => row.requested !== undefined || row.responded !== undefined);
      return;
    }

    const request = this.readRequest(requestBody);
    this.rows = [
      { label: 'Belge toplamı', requested: request.documentTotal, responded: response.documentTotal, isMoney: true },
      { label: 'Satırların toplamı', requested: request.lineTotal, responded: response.lineTotal, isMoney: true },
      { label: 'Kart ödemesi', requested: request.paymentTotal, responded: response.paymentTotal, isMoney: true },
      { label: 'Taksit', requested: request.installmentCount, responded: response.installmentCount, isMoney: false },
    ].filter(row => row.requested !== undefined || row.responded !== undefined);
  }

  format(row: AmountCompareRow, value?: number): string {
    if (value === undefined) return '—';
    return row.isMoney ? moneyFormat.format(value) : String(value);
  }

  /** Fark = cevap - istek; iki taraf da yoksa null. */
  difference(row: AmountCompareRow): number | null {
    if (row.requested === undefined || row.responded === undefined) return null;
    return Math.round((row.responded - row.requested) * 100) / 100;
  }

  isMatch(row: AmountCompareRow): boolean {
    const difference = this.difference(row);
    return difference !== null && Math.abs(difference) < TOLERANCE;
  }

  hasMismatch(): boolean {
    return this.rows.some(row => this.difference(row) !== null && !this.isMatch(row));
  }

  // ── İstek: PosSaleRequest / PosVoidRequest ─────────────────────────────

  private isRefundRequest(request: any): boolean {
    return !!request && typeof request === 'object' && 'LineNos' in request;
  }

  private isVoidRequest(request: any): boolean {
    return !!request && typeof request === 'object' && ('ProviderSaleNumber' in request || 'Original' in request);
  }

  private readRequest(request: any): AmountSide {
    if (!request || typeof request !== 'object') return {};
    const lines = this.array(request.Lines);
    const payments = this.array(request.Payments);
    return {
      documentTotal: this.number(request.TotalAmount),
      lineTotal: lines.length ? this.sum(lines, 'TotalPrice') : undefined,
      paymentTotal: payments.length ? this.sum(payments, 'Amount') : undefined,
      installmentCount: this.maxInstallment(payments.map(p => p.InstallmentCount)),
    };
  }

  // ── Cevap: sağlayıcının satış nesnesi ───────────────────────────────────

  private readResponse(response: any): AmountSide {
    if (!response || typeof response !== 'object') return {};
    const sale = this.findSale(response);
    if (!sale) {
      // Satış henüz sonuçlanmadı (ör. link yeni oluşturuldu): yalnızca istenen kart tutarı döner.
      return { paymentTotal: this.number(this.findValue(response, 'PaymentAmount')) };
    }
    const items = this.array(sale.AddedSaleItems);
    // Aynı listede başarısız denemeler (temassız okunamadı → çip) ve iptal sonrası karta iade
    // kalemleri de gelir: kart ödemesi yalnızca tamamlanmış satış kalemleridir. Durum bilgisi
    // olmayan kalem (sağlayıcı göndermediyse) tamamlanmış sayılır.
    const allPayments = this.array(sale.AddedPayments);
    const isCompleted = (p: any) => p.StatusId === undefined || p.StatusId === null || COMPLETED_PAYMENT_STATUSES.includes(p.StatusId);
    const isRefund = (p: any) => p.OperationTypeId === REFUND_OPERATION;
    const payments = allPayments.filter(p => !isRefund(p) && isCompleted(p));
    const refunds = allPayments.filter(p => isRefund(p) && isCompleted(p));
    return {
      documentTotal: this.number(sale.TotalPrice),
      lineTotal: items.length ? this.sum(items, 'TotalPriceAmount') : undefined,
      paymentTotal: payments.length ? this.sum(payments, 'PaymentAmount') : undefined,
      installmentCount: this.maxInstallment(payments.map(p =>
        p.InstallmentCount ?? unwrapJson(p.OnlinePayment)?.InstallmentCount)),
      refundTotal: refunds.length ? this.sum(refunds, 'PaymentAmount') : undefined,
    };
  }

  /**
   * Satış özetini taşıyan ilk nesne. "Request" alanı (cihaza giden istek) atlanır;
   * yoksa cevabın değil isteğin satırları okunurdu.
   */
  private findSale(node: any): any {
    node = unwrapJson(node);
    if (!node || typeof node !== 'object') return null;
    if (Array.isArray(node)) {
      for (const item of node) {
        const found = this.findSale(item);
        if (found) return found;
      }
      return null;
    }
    if ('AddedPayments' in node || 'AddedSaleItems' in node || 'SaleNumber' in node) return node;
    for (const [key, value] of Object.entries(node)) {
      if (key === 'Request') continue;
      const found = this.findSale(value);
      if (found) return found;
    }
    return null;
  }

  private findValue(node: any, key: string): any {
    node = unwrapJson(node);
    if (!node || typeof node !== 'object') return undefined;
    if (!Array.isArray(node) && key in node) return node[key];
    for (const [childKey, value] of Object.entries(node)) {
      if (childKey === 'Request') continue;
      const found = this.findValue(value, key);
      if (found !== undefined) return found;
    }
    return undefined;
  }

  // ── Yardımcılar ─────────────────────────────────────────────────────────

  private parse(json?: string | null): any {
    return json ? tryParseJson(json) : undefined;
  }

  private array(value: any): any[] {
    const unwrapped = unwrapJson(value);
    return Array.isArray(unwrapped) ? unwrapped.map(item => unwrapJson(item)).filter(item => item && typeof item === 'object') : [];
  }

  private number(value: any): number | undefined {
    const parsed = typeof value === 'string' ? Number(value) : value;
    return typeof parsed === 'number' && isFinite(parsed) ? parsed : undefined;
  }

  private sum(items: any[], key: string): number | undefined {
    const values = items.map(item => this.number(item[key])).filter((v): v is number => v !== undefined);
    return values.length ? Math.round(values.reduce((total, v) => total + v, 0) * 100) / 100 : undefined;
  }

  private maxInstallment(values: any[]): number | undefined {
    const counts = values.map(v => this.number(v)).filter((v): v is number => v !== undefined);
    return counts.length ? Math.max(...counts) : undefined;
  }
}
